import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import {
  COLOR,
  FONT_SIZE,
  FONT_WEIGHT,
  RADIUS,
  SPACING,
} from 'constants/design-system';
import { capitalize, isEmpty } from 'lodash';
import { DateTime } from 'luxon';

import {
  ActionButtonOrder,
  HasUnacceptChangesLabel,
} from 'modules/ChefScene/components/ActionButton';
import { RouteNames } from 'navigation/linkingConfig';
import { useAppSelector } from 'store/hooks';
import { NotificationsSelectors } from 'store/Notifications';
import {
  ItemPreparationStatuses,
  OrderInternalStatuses,
} from 'store/Orders/enums';
import {
  checkIfScheduledOrder,
  getOrderInternalStatus,
} from 'store/Orders/utils';
import { SettingSelectors } from 'store/Settings';
import { UserSelectors } from 'store/User';
import { AnalyticsEvents, useLogEventCallback } from 'util/analytics';
import { getAddressLine, useFormatDateTime } from 'util/helpers';
import { useCurrencyString } from 'util/helpers/currency';
import { useSumOrderItems } from 'util/hooks';
import { BaseText } from 'components/BaseText';
import { OrderStartPreparationStatusBadge } from 'components/StatusBadge';

import { BadgeSection } from './components/BadgeSection';
import { HeaderText } from './components/HeaderText';
import { useHeaderDisplaySettings } from './useHeaderDisplaySettings';
import type { StyleProp, ViewStyle } from 'react-native';
import type { Order, OrderItem } from 'store/Orders/types';

export type HeaderProps = {
  order: Order;
  items: OrderItem[];
  style?: StyleProp<ViewStyle>;
  leftHeaderStyle?: StyleProp<ViewStyle>;
  selectedOrderItemUuid: string | undefined;
  isAllHeaderOptionVisible?: boolean;
  //START: COPIED from old components
  // orderInternalStatus: OrderInternalStatuses;
  onStatusChanged?: (props: {
    orderId: string;
    itemId: string;
    mode: 'single' | 'group';
    startDate: Date;
    endDate: Date;
    status: ItemPreparationStatuses;
    screen: string | undefined;
  }) => void;
  onPressedAction?: () => void;
  //END: COPIED from old components
};

export const Header = (props: HeaderProps) => {
  const {
    order,
    items,
    style,
    leftHeaderStyle,
    onStatusChanged,
    onPressedAction,
    selectedOrderItemUuid,
    isAllHeaderOptionVisible,
  } = props;

  const {
    recipient,
    description,
    remake_for_order_uuid,
    remake_for_order_description,
    dropoff_location,
    kds_custom_fields,
  } = order;

  const { t } = useTranslation();
  const getCurrencyString = useCurrencyString();

  const itemCount = useSumOrderItems(items);

  const featureFlags = useAppSelector(UserSelectors.selectFeatureFlags);

  const formatDateTime = useFormatDateTime();

  const {
    shouldDisplayCustomFields,
    shouldDisplayCustomerAddress,
    shouldDisplayCustomerName,
    shouldDisplayCustomerPhoneNumber,
    shouldDisplayNumberOfItems,
    shouldDisplayTotalOrderValue,
  } = useHeaderDisplaySettings({
    ignoreDeviceSetting: isAllHeaderOptionVisible,
  });

  const { shouldDisplayAllActionButton } = useAppSelector(
    SettingSelectors.selectActionButtonsInterfaceSettings,
  );

  // const shouldShowFinancialSection = featureFlags.display_financial_info;

  const _formatDateTime = (date: string) => {
    return `${formatDateTime(date, DateTime.TIME_SIMPLE)} - ${formatDateTime(
      date,
      {
        month: 'short',
        day: 'numeric',
      },
    )}`;
  };

  const orderInternalStatus = useMemo(() => {
    return getOrderInternalStatus(order, items);
  }, [order, items]);

  const logEvent = useLogEventCallback();
  const isScheduledFutureOrder = checkIfScheduledOrder(order);

  const hasUnacceptedNotifications = useAppSelector((state) =>
    NotificationsSelectors.selectOrderHasUnattended(state, order.uuid),
  );
  const { shouldDisplayActionButtonHeader } = useAppSelector(
    SettingSelectors.selectOrderDetailsInterfaceSettings,
  );

  const shouldShowActionButton =
    shouldDisplayAllActionButton &&
    selectedOrderItemUuid === undefined &&
    orderInternalStatus !== OrderInternalStatuses.CANCELLED &&
    (hasUnacceptedNotifications ||
      (shouldDisplayActionButtonHeader &&
        orderInternalStatus !== OrderInternalStatuses.PREPARED));

  const getStatusChangeCallback =
    (params?: Dict) =>
    (
      statusToUpdate: ItemPreparationStatuses,
      itemsToUpdate: OrderItem[],
      duration: number,
      startDate: Date,
      endDate: Date,
    ) => {
      onPressedAction?.();
      logEvent(
        AnalyticsEvents.itemStatusChanged({
          isScheduled: isScheduledFutureOrder,
          screen: RouteNames.Home.Main.Drawer.Tabs.Overview.OrderDetails,
          status: statusToUpdate,
          orderId: order.uuid,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          executionTime: duration,
          ...params,
        }),
      );

      for (const item of itemsToUpdate) {
        onStatusChanged?.({
          startDate,
          endDate,
          status: statusToUpdate,
          itemId: item.uuid,
          mode: 'group',
          orderId: item.order_uuid,
          screen: RouteNames.Home.Main.Drawer.Tabs.Overview.OrderDetails,
        });
      }
    };

  return (
    <View style={[{ flexDirection: 'row', gap: SPACING.theme[8] }, style]}>
      <View style={[{ flex: 1, gap: SPACING.theme[4] }, leftHeaderStyle]}>
        <BaseText
          fontSize={FONT_SIZE.theme.lg}
          color={COLOR.theme.white}
          fontWeight={FONT_WEIGHT.theme.bold}
        >
          {`#${description}`}
        </BaseText>

        {remake_for_order_uuid && remake_for_order_description && (
          <BaseText
            fontWeight={FONT_WEIGHT.theme.bold}
            color={'#fff8bf'}
            fontSize={FONT_SIZE.theme.lg}
          >
            ({t('overview.orderDetails.remakeFor')}#
            {remake_for_order_description})
          </BaseText>
        )}
        <View style={{ height: SPACING.theme[2] }} />
        <HeaderText title={_formatDateTime(order.created_at)} />
        {selectedOrderItemUuid === undefined &&
          shouldDisplayTotalOrderValue && (
            <HeaderText
              title={getCurrencyString(
                order.financial_info.grand_total_including_tax,
              )}
            />
          )}

        {selectedOrderItemUuid === undefined && shouldDisplayNumberOfItems && (
          <HeaderText
            title={t('overview.drawer.orderItem.items', { count: itemCount })}
          />
        )}

        <>
          {shouldDisplayCustomerName && (
            <HeaderText title={recipient.full_name} />
          )}
          {shouldDisplayCustomerPhoneNumber && (
            <HeaderText title={recipient.phone_number} />
          )}
          {dropoff_location && shouldDisplayCustomerAddress && (
            <HeaderText title={getAddressLine(dropoff_location)} />
          )}
        </>

        {shouldDisplayCustomFields &&
          !isEmpty(kds_custom_fields) &&
          kds_custom_fields!.map(({ key, value }, index) => {
            return (
              <HeaderText key={index} title={`${capitalize(key)}: ${value}`} />
            );
          })}
      </View>
      <View
        style={{
          flex: 1,
          // TODO: change to theme[4] after changing action button component
          gap: SPACING.theme[4],
          justifyContent: 'flex-start',
        }}
      >
        <BadgeSection
          isAllHeaderOptionVisible={isAllHeaderOptionVisible}
          order={order}
        />

        {featureFlags.kds_enabled_traffic_light_system &&
          orderInternalStatus === OrderInternalStatuses.NOT_BEING_PREPARED && (
            <OrderStartPreparationStatusBadge
              status={order.start_preparation_status}
              iconSize={FONT_SIZE.theme.lg}
              textSize={FONT_SIZE.theme.sm}
              style={{
                borderWidth: 1,
                borderColor: COLOR.theme.white,
                borderRadius: RADIUS.theme.md,
                // NOTE: remove default margin
                marginBottom: 0,
              }}
            />
          )}

        {shouldShowActionButton && (
          <View
            style={{
              alignItems: 'center',
              justifyContent: 'flex-start',
              // FIXME: - hardcoded due to ActionButton component has weird internal flex
              height: 76,
            }}
          >
            {hasUnacceptedNotifications ? (
              <HasUnacceptChangesLabel color={'white'} />
            ) : shouldDisplayActionButtonHeader &&
              orderInternalStatus !== OrderInternalStatuses.PREPARED ? (
              <ActionButtonOrder
                includePrintButton
                order={order}
                items={items}
                isScheduled={isScheduledFutureOrder}
                onPressFinish={getStatusChangeCallback({ print: false })}
                onPressFinishAndPrint={getStatusChangeCallback({
                  print: true,
                })}
              />
            ) : null}
          </View>
        )}
      </View>
    </View>
  );
};
